import React, { useState } from 'react';
import {
  FileText,
  BarChart3,
  DollarSign,
  Download,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AllCampaignsSummary from './AllCampaignsSummary';
import DetailedCampaignReport from './DetailedCampaignReport';
import CashBoxReport from './CashBoxReport';
import DateInput from '../common/DateInput';

const ReportsManagement = () => {
  const { t } = useTranslation();
  const [activeReport, setActiveReport] = useState('summary');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    endDate: new Date().toISOString().split('T')[0] // Today
  });
  const [isGenerating, setIsGenerating] = useState(false);

  const reportTypes = [
    {
      id: 'summary',
      title: t('reports.allCampaignsSummary'),
      description: t('reports.summaryDescription'),
      icon: BarChart3,
      color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
    },
    {
      id: 'detailed',
      title: t('reports.detailedCampaignReport'),
      description: t('reports.detailedDescription'),
      icon: FileText,
      color: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
    },
    {
      id: 'cashbox',
      title: t('reports.cashBoxReport'),
      description: t('reports.cashBoxDescription'),
      icon: DollarSign,
      color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400'
    }
  ];

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success(t('reports.reportGenerated'));
    } catch (error) {
      toast.error(t('reports.reportError'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportReport = (format) => {
    toast.success(t('reports.exportStarted', { format: format.toUpperCase() }));
    // Simulate export
    setTimeout(() => {
      toast.success(t('reports.exportCompleted', { format: format.toUpperCase() }));
    }, 1500);
  };

  const renderActiveReport = () => {
    switch (activeReport) {
      case 'summary':
        return <AllCampaignsSummary dateRange={dateRange} />;
      case 'detailed':
        return <DetailedCampaignReport selectedCampaign={1} dateRange={dateRange} />;
      case 'cashbox':
        return <CashBoxReport dateRange={dateRange} />;
      default:
        return <AllCampaignsSummary dateRange={dateRange} />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('reports.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('reports.subtitle')}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? t('reports.generating') : t('reports.generateReport')}
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={() => handleExportReport('pdf')}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              PDF
            </button>
            <button
              onClick={() => handleExportReport('excel')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              Excel
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">{t('reports.dateRange')}</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                {t('reports.startDate')}
              </label>
              <DateInput
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                {t('reports.endDate')}
              </label>
              <DateInput
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isActive = activeReport === report.id;
          
          return (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id)}
              className={`p-6 rounded-xl border-2 transition-all duration-200 text-left ${
                isActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${report.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`font-semibold mb-2 ${
                    isActive ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                  }`}>
                    {report.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${
                    isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {report.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active Report Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {renderActiveReport()}
      </div>
    </div>
  );
};

export default ReportsManagement;